// ============================================================================
// Marcas sobre la misma plataforma (CC-CONSOLE #1535)
// ============================================================================
// Wayak Logistics (B2B, flotas) y Movik (B2C, acompañamiento de trayectos de
// personas) comparten API, datos y consola; cada una tiene su app nativa y su
// sitio. Espejo de `apps/torre-de-control-api/functions/src/utils/brands.js`
// (la prueba de paridad `brands.parity.test.ts` cuida que coincidan).
//
//   • `companies.brand` → "wayak" | "movik"            (omisión "wayak")
//   • `licenses.brand`  → "wayak" | "movik" | "both"   (omisión "wayak")
// ============================================================================

export const BRANDS = ["wayak", "movik"] as const;
export type Brand = (typeof BRANDS)[number];
export const DEFAULT_BRAND: Brand = "wayak";

/** Una licencia puede venderse en una marca o en las dos. */
export const LICENSE_BRANDS = [...BRANDS, "both"] as const;
export type LicenseBrand = (typeof LICENSE_BRANDS)[number];

export interface BrandInfo {
  id: Brand;
  /** Nombre comercial. */
  name: string;
  /** Producto con el que el CRM central la distingue. */
  product: string;
  /** Color principal y tinta de la marca. */
  colors: { primary: string; ink: string };
  /** Sitio público por entorno. */
  urls: { prod: string; dev: string };
}

export const BRAND_INFO: Record<Brand, BrandInfo> = {
  wayak: {
    id: "wayak",
    name: "Wayak Logistics",
    product: "Wayak",
    // Paleta oficial: accent #0084D5 (Pantone 2173 C), navy #26303A (432 C).
    colors: { primary: "#0084D5", ink: "#26303A" },
    urls: { prod: "https://wayak-logistics.com", dev: "https://wayak-landing.web.app" },
  },
  movik: {
    id: "movik",
    name: "Movik",
    product: "Movik",
    // Colores del logo de Movik: azul del pin y tinta de las letras.
    colors: { primary: "#2E86CE", ink: "#1F2A35" },
    urls: { prod: "https://movik-production.web.app", dev: "https://movik-development.web.app" },
  },
};

const clean = (value: unknown): string => (typeof value === "string" ? value.trim().toLowerCase() : "");

/** Marca válida o la de omisión: nada fuera de la lista. */
export function normalizeBrand(value: unknown): Brand {
  const brand = clean(value);
  return (BRANDS as readonly string[]).includes(brand) ? (brand as Brand) : DEFAULT_BRAND;
}

/** Marca de una licencia (`both` incluido); omisión "wayak". */
export function normalizeLicenseBrand(value: unknown): LicenseBrand {
  const brand = clean(value);
  return (LICENSE_BRANDS as readonly string[]).includes(brand) ? (brand as LicenseBrand) : DEFAULT_BRAND;
}

/** "Movik" para movik, "Wayak" para todo lo demás. */
export function productForBrand(value: unknown): string {
  return BRAND_INFO[normalizeBrand(value)].product;
}

/**
 * ¿Esta consola corre en un entorno de desarrollo? Mismo criterio que la
 * landing: localhost, el proyecto de dev y los canales de vista previa.
 */
export function isDevHostname(hostname: string): boolean {
  if (["localhost", "127.0.0.1"].includes(hostname)) return true;
  if (/development/.test(hostname)) return true;
  return /--[\w-]+\.(web\.app|firebaseapp\.com)$/.test(hostname);
}

/** URL pública de la marca para el entorno de `hostname`. */
export function brandUrl(value: unknown, hostname: string = typeof window !== "undefined" ? window.location.hostname : ""): string {
  const info = BRAND_INFO[normalizeBrand(value)];
  return isDevHostname(hostname) ? info.urls.dev : info.urls.prod;
}
